import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IStatus } from "shared/types";
import { List } from "shared/components";
import { copy } from "shared/assets";

import { PATHS } from "App/router";

import { AUTH_ACTIONS } from "Auth/types";
import { deleteAccount, logout } from "Auth/state";
import { useStateActionMeta } from "Auth/utils";
import { DeleteAccount as DeleteAccountComponent } from "Auth/components";

import {
  dataMetaSelector as venueMetaSelector,
  isOnlyVenueUser,
} from "Venues/state";
import { VENUE_ACTIONS } from "Venues/types";
import {
  dataMetaSelector as bandMetaSelector,
  isOnlyBandUser,
} from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import { useProfileContext } from "Auth/contexts";

interface DeleteAccountProps {
  handleClose: () => void;
}

export const DeleteAccount: React.FC<DeleteAccountProps> = ({
  handleClose,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const meta = useStateActionMeta(AUTH_ACTIONS.deleteAccount);
  const { api: onlyVenueUserMeta } = useSelector(
    venueMetaSelector(VENUE_ACTIONS.isOnlyVenueUser)
  );

  const { api: onlyBandUserMeta } = useSelector(
    bandMetaSelector(BAND_ACTIONS.isOnlyBandUser)
  );

  const { currentUser } = useProfileContext();

  useEffect(() => {
    dispatch(isOnlyVenueUser());
    currentUser?.id && dispatch(isOnlyBandUser({ userId: currentUser.id }));
  }, [dispatch, currentUser]);

  useEffect(() => {
    if (meta?.status === IAPIState.succeeded) {
      dispatch(logout(true));
      history.push(PATHS.HOME);
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: copy.deleteAccount.success,
        })
      );
    }
  }, [dispatch, meta, history]);

  const handleDelete = () => {
    dispatch(deleteAccount());
  };

  const deleteMessage = useMemo(
    () => (
      <List>
        {onlyVenueUserMeta?.status === IAPIState.failed && (
          <li>{copy.deleteAccount.venuesMessage}</li>
        )}
        {onlyBandUserMeta?.status === IAPIState.failed && (
          <li>{copy.deleteAccount.bandsMessage}</li>
        )}
      </List>
    ),
    [onlyVenueUserMeta?.status, onlyBandUserMeta?.status]
  );

  return (
    <DeleteAccountComponent
      isLoading={
        onlyVenueUserMeta?.status === IAPIState.pending ||
        onlyBandUserMeta?.status === IAPIState.pending
      }
      preventDeleteAccount={
        onlyVenueUserMeta?.status === IAPIState.failed ||
        onlyBandUserMeta?.status === IAPIState.failed
      }
      preventDeleteMessage={deleteMessage}
      handleClose={handleClose}
      handleDelete={handleDelete}
      metaKey={AUTH_ACTIONS.deleteAccount}
    />
  );
};
